import * as React from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  PhoneCall,
  Plus,
  Sparkles,
  Wrench,
  WandSparkles,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { AgentDocumentsSelector } from '@/components/agents/AgentDocumentsSelector'
import { AgentTimezonePicker } from '@/components/agents/AgentTimezonePicker'
import { AgentVisualEditor, AgentAvatar, AgentVisualIcon } from '@/components/agents/AgentVisualEditor'
import { PromptRichEditor } from '@/components/agents/PromptRichEditor'
import {
  buildSelectedToolDynamicKeys,
  buildSystemMentionSuggestions,
  buildToolMentionSuggestions,
  findDuplicateSelectedToolDynamicKeys,
  findUnknownReferencedDynamicTokens,
} from '@/components/agents/promptMentionsShared'
import { ToolForm } from '@/components/tools/ToolForm'
import { WizardSidebarStep } from '@/components/wizard/WizardSidebarStep'
import { getWizardStepStatus } from '@/components/wizard/step-status'
import {
  AGENT_STUDIO_TOTAL_STEPS,
  clearAgentStudioPersistedDraft,
  createInitialAgentDraft,
  saveAgentStudioPersistedDraft,
  useAgentStudioPersistedState,
  type AgentDraft,
} from '@/hooks/studio/useAgentStudioPersistence'
import { normalizeAgentJsonPayload, validateAgentJsonPayload, validateAgentJsonText } from '@/lib/agent-json-validation'
import {
  openaiReasoningEffortForApiPayload,
  parseModelId,
} from '@/lib/agent-models'
import { getFirstCatalogModelId, isModelIdInCatalog } from '@/lib/agent-model-ui'
import { saveAgentVisual, type StoredAgentVisual } from '@/lib/agent-visuals'
import { DEFAULT_AGENT_TIMEZONE } from '@/lib/timezones'
import { loadToolIconsMap, type StoredToolIcon } from '@/lib/tool-icons'
import { cn } from '@/lib/utils'
import { getToolVisibility, herramientaBloqueadaPorAudiencia } from '@/lib/toolVisibility'
import { useIsMobile } from '@/hooks/use-mobile'
import { agentsService } from '@/services/agentsService'
import { tagService, type Tag } from '@/services/TagService'
import { toolsService } from '@/services/toolsService'
import type {
  Agent,
  AgentAudience,
  AgentAvailableModel,
  AgentAvailableModelsResponse,
  AgentAvailableDocument,
  AgentDocument,
} from '@/types/agent'
import type { MCPAction } from '@/types/tool'

const DEFAULT_BASE_PROMPT =
  'Eres un asistente especializado. Responde de forma clara y usa herramientas cuando aporten valor.'

const PROMPT_TEMPLATES_ES = [
  {
    label: 'Atencion comercial',
    value:
      [
        '# Rol',
        'Eres un agente comercial senior de una inmobiliaria premium.',
        '',
        '# Objetivo',
        'Convertir cada conversacion en un siguiente paso concreto (visita, llamada o envio de propuesta) sin friccion.',
        '',
        '# Flujo obligatorio',
        '1) Detecta intencion principal y contexto del cliente en la primera respuesta.',
        '2) Si faltan datos criticos, haz como maximo 2 preguntas concretas y utiles.',
        '3) Usa herramientas cuando puedan aportar datos verificables para avanzar la conversacion.',
        '4) Cierra cada respuesta con una recomendacion accionable y una llamada a la accion.',
        '',
        '# Reglas de calidad',
        '- No inventes datos ni disponibilidad.',
        '- Si falta informacion o hay incertidumbre, dilo claramente y propone el siguiente mejor paso.',
        '- Responde en espanol claro, cercano y profesional.',
        '- Mantente breve: 2-5 frases, sin relleno.',
        '',
        '# Formato de salida',
        'Responde siempre con este hay que elorden:',
        '1) Respuesta al cliente',
        '2) Siguiente accion sugerida',
      ].join('\n'),
  },
  {
    label: 'Soporte operativo',
    value:
      [
        '# Rol',
        'Eres un agente de soporte operativo experto en resolver incidencias sin romper procesos.',
        '',
        '# Objetivo',
        'Resolver la incidencia en el menor numero de turnos posible, con trazabilidad y sin asumir datos.',
        '',
        '# Flujo obligatorio',
        '1) Resume el problema en una frase para confirmar entendimiento.',
        '2) Verifica primero los datos minimos necesarios.',
        '3) Ejecuta la herramienta adecuada y comunica solo resultados verificables.',
        '4) Si no se puede resolver, escala con una recomendacion clara.',
        '',
        '# Reglas de seguridad',
        '- Nunca pidas ni expongas credenciales, tokens o datos sensibles.',
        '- No inventes estados de sistema ni resultados de herramientas.',
        '- Si una herramienta falla, informa el fallo y propone alternativa.',
        '',
        '# Formato de salida',
        'Usa siempre:',
        '1) Estado actual',
        '2) Accion ejecutada',
        '3) Resultado',
        '4) Proximo paso',
      ].join('\n'),
  },
  {
    label: 'Captacion de leads',
    value:
      [
        '# Rol',
        'Eres un agente de captacion y cualificacion de leads orientado a conversion.',
        '',
        '# Objetivo',
        'Cualificar rapido y llevar al lead a una accion de alto valor (visita, llamada, reserva de cita).',
        '',
        '# Criterios de cualificacion',
        'Recoge de forma natural: zona de interes, presupuesto, plazo, tipo de operacion y nivel de decision.',
        '',
        '# Flujo obligatorio',
        '1) Empieza con una apertura breve y personalizada.',
        '2) Haz preguntas cortas y una por turno si la informacion es incompleta.',
        '3) Si una herramienta puede acelerar la cualificacion, usala.',
        '4) Cierra con propuesta concreta de siguiente paso y confirmacion.',
        '',
        '# Reglas de calidad',
        '- No hagas interrogatorios largos ni preguntas redundantes.',
        '- No inventes informacion comercial.',
        '- Si no puedes avanzar, explica por que y que falta para continuar.',
        '',
        '# Formato de salida',
        '1) Respuesta al lead',
        '2) Datos clave capturados',
        '3) Siguiente accion sugerida',
      ].join('\n'),
  },
] as const

const PROMPT_TEMPLATES_EN = [
  {
    label: 'Commercial support',
    value:
      [
        '# Role',
        'You are a senior commercial real-estate agent assistant.',
        '',
        '# Goal',
        'Turn each conversation into a concrete next step (visit, call, or proposal) with minimal friction.',
        '',
        '# Required flow',
        '1) Identify the main intent and context in the first reply.',
        '2) If key data is missing, ask at most 2 concise questions.',
        '3) Use tools whenever they can provide verifiable data to move forward.',
        '4) End every message with one actionable recommendation and CTA.',
        '',
        '# Quality rules',
        '- Do not invent availability or facts.',
        '- If information is uncertain, be explicit and propose the best next step.',
        '- Keep responses concise, clear, and professional.',
        '- Keep it short: 2-5 sentences.',
        '',
        '# Output format',
        '1) Reply to the user',
        '2) Suggested next action',
      ].join('\n'),
  },
  {
    label: 'Operational support',
    value:
      [
        '# Role',
        'You are an operations support assistant focused on resolution speed and reliability.',
        '',
        '# Goal',
        'Resolve incidents in the fewest turns possible, with traceability and no assumptions.',
        '',
        '# Required flow',
        '1) Summarize the issue in one sentence.',
        '2) Validate minimum required data first.',
        '3) Execute the right tool and report only verified results.',
        '4) If unresolved, escalate with a clear next step.',
        '',
        '# Safety rules',
        '- Never request or expose secrets or credentials.',
        '- Do not invent system states or tool outcomes.',
        '- If a tool fails, report it and provide an alternative.',
        '',
        '# Output format',
        '1) Current status',
        '2) Action executed',
        '3) Result',
        '4) Next step',
      ].join('\n'),
  },
  {
    label: 'Lead capture',
    value:
      [
        '# Role',
        'You are a lead qualification assistant focused on conversion.',
        '',
        '# Goal',
        'Quickly qualify leads and move them to a high-value action (visit, call, booked slot).',
        '',
        '# Qualification criteria',
        'Collect naturally: area of interest, budget, timeline, operation type, decision readiness.',
        '',
        '# Required flow',
        '1) Start with a brief personalized opener.',
        '2) Ask one short question per turn when data is incomplete.',
        '3) Use tools when they can accelerate qualification.',
        '4) Close with one concrete next step and confirmation.',
        '',
        '# Quality rules',
        '- Avoid long interrogations and redundant questions.',
        '- Do not invent commercial data.',
        '- If blocked, explain why and what is needed to continue.',
        '',
        '# Output format',
        '1) Reply to the lead',
        '2) Captured key data',
        '3) Suggested next action',
      ].join('\n'),
  },
] as const

// Los colores del confeti al crear un agente. Es decoracion de una celebracion
// que dura segundo y medio: no son estado ni categoria, y un token no dice nada
// aqui. Se quedan, declarados.
// eslint-disable-next-line no-restricted-syntax -- colores de confeti: decoracion de una celebracion, sin significado
const CONFETTI_COLORS = ['#22d3ee', '#60a5fa', '#facc15', '#fb7185', '#34d399', '#a78bfa']

function slugifyAgentName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeId(value: string | number): string {
  return String(value)
}

function hasValidAgentId(agent: Partial<Agent> | null | undefined): agent is Partial<Agent> & { id: string } {
  return Boolean(agent?.id)
}

function mergePromptParts(basePrompt: string, customInstructions: string): string {
  return [basePrompt.trim(), customInstructions.trim()].filter(Boolean).join('\n\n')
}

const NAME_COLLISION_ERROR = '__NAME_COLLISION__'

function hasNameCollisionInText(value: unknown): boolean {
  const text = String(value ?? '').toLowerCase()
  return (
    (text.includes('name') || text.includes('nombre')) &&
    (
      text.includes('already exists')
      || text.includes('ya existe')
      || text.includes('must make a unique')
      || text.includes('unique constraint')
      || text.includes('duplicate')
    )
  )
}

function toErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'body' in error) {
    const body = (error as { body?: unknown }).body
    if (body && typeof body === 'object') {
      const wrappedError = (body as { error?: unknown }).error
      if (wrappedError && typeof wrappedError === 'object') {
        const details = (wrappedError as { details?: unknown }).details
        const nameDetails =
          details && typeof details === 'object'
            ? (details as Record<string, unknown>).name
            : undefined
        if (nameDetails !== undefined && hasNameCollisionInText(nameDetails)) {
          return NAME_COLLISION_ERROR
        }
      }
      const bodyName = (body as { name?: unknown }).name
      if (bodyName !== undefined && hasNameCollisionInText(bodyName)) {
        return NAME_COLLISION_ERROR
      }
      const entries = Object.entries(body as Record<string, unknown>)
      if (entries.length > 0) {
        const [field, value] = entries[0]
        if (field === 'name' && hasNameCollisionInText(value)) {
          return NAME_COLLISION_ERROR
        }
        if (Array.isArray(value) && value[0]) {
          return `${field}: ${String(value[0])}`
        }
        return `${field}: ${String(value)}`
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'Ha ocurrido un error inesperado.'
}

type StepLayoutProps = {
  title: string
  subtitle: string
  children: React.ReactNode
  footer: React.ReactNode
  isMobile?: boolean
}

function StepLayout({ title, subtitle, children, footer, isMobile }: StepLayoutProps) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      {!isMobile && (
        <div className="border-b border-border px-6 py-5 md:px-8">
          <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground md:text-sm">{subtitle}</p>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="mx-auto w-full max-w-5xl p-6 md:p-8">{children}</div>
        </ScrollArea>
      </div>

      <div className="border-t border-border bg-muted/10 px-6 py-4 md:px-8">{footer}</div>
    </div>
  )
}

function CelebrationConfetti({ seed }: { seed: number }) {
  const pieces = React.useMemo(() => {
    const seededRandom = (value: number): number => {
      const x = Math.sin((value + 1) * (seed + 1) * 12.9898) * 43758.5453
      return x - Math.floor(x)
    }

    return Array.from({ length: 80 }, (_, index) => ({
      id: index,
      left: seededRandom(index) * 100,
      xDrift: (seededRandom(index + 17) - 0.5) * 180,
      rotation: (seededRandom(index + 41) - 0.5) * 540,
      duration: 1.9 + seededRandom(index + 73) * 1.2,
      delay: seededRandom(index + 99) * 0.25,
      size: 6 + seededRandom(index + 131) * 7,
      color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    }))
  }, [seed])

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((piece) => (
        <motion.span
          key={`${seed}-${piece.id}`}
          className="absolute rounded-md"
          style={{
            left: `${piece.left}%`,
            top: -24,
            width: piece.size,
            height: piece.size * 0.55,
            backgroundColor: piece.color,
          }}
          initial={{ y: -20, x: 0, rotate: 0, opacity: 0 }}
          animate={{ y: 980, x: piece.xDrift, rotate: piece.rotation, opacity: [0, 1, 1, 0] }}
          transition={{ duration: piece.duration, delay: piece.delay, ease: 'easeOut' }}
        />
      ))}
    </div>
  )
}

export function AgentStudioFlowPage() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()
  const persistedState = useAgentStudioPersistedState(DEFAULT_BASE_PROMPT)

  const [step, setStep] = React.useState(persistedState?.step ?? 1)
  const [visitedSteps, setVisitedSteps] = React.useState<Set<number>>(
    () => new Set(persistedState?.visitedSteps?.length ? persistedState.visitedSteps : [1])
  )
  const [toolsLoading, setToolsLoading] = React.useState(true)
  const [creatingAgent, setCreatingAgent] = React.useState(false)
  const [toolFormOpen, setToolFormOpen] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [searchTools, setSearchTools] = React.useState('')
  const [confettiSeed, setConfettiSeed] = React.useState(0)
  const [editingField, setEditingField] = React.useState<'display_name' | null>(null)
  const [finishing, setFinishing] = React.useState(false)
  const [visual, setVisual] = React.useState<StoredAgentVisual>(persistedState?.visual ?? {})
  const [coverPrompt, setCoverPrompt] = React.useState(persistedState?.coverPrompt ?? '')
  const [coverStyle, setCoverStyle] = React.useState(persistedState?.coverStyle ?? 'cinematic')
  const [coverGenerating, setCoverGenerating] = React.useState(false)
  const [coverError, setCoverError] = React.useState<string | null>(null)
  const [selectedModel, setSelectedModel] = React.useState(
    persistedState?.selectedModel ?? ''
  )
  const [availableModelOptions, setAvailableModelOptions] = React.useState<AgentAvailableModel[]>([])
  const [mediaProcessingModel, setMediaProcessingModel] = React.useState<AgentAvailableModel | null>(null)
  const [loadingAvailableModels, setLoadingAvailableModels] = React.useState(false)
  const [availableModelsError, setAvailableModelsError] = React.useState<string | null>(null)
  const [editorMode, setEditorMode] = React.useState<'form' | 'json'>(persistedState?.editorMode ?? 'form')
  const [jsonDraft, setJsonDraft] = React.useState(persistedState?.jsonDraft ?? '')
  const [jsonError, setJsonError] = React.useState<string | null>(null)

  const [tools, setTools] = React.useState<MCPAction[]>([])
  const [availableTags, setAvailableTags] = React.useState<Tag[]>([])
  const [toolIcons, setToolIcons] = React.useState<Record<string, StoredToolIcon>>(loadToolIconsMap)
  const [createdAgent, setCreatedAgent] = React.useState<Agent | null>(null)
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<AgentDraft>(() => persistedState?.draft ?? createInitialAgentDraft(DEFAULT_BASE_PROMPT))
  const [documentCatalog, setDocumentCatalog] = React.useState<Record<number, AgentDocument>>({})
  const langPrefix = `/${lang || 'en'}`
  const isSpanish = (lang || i18n.language || 'en').toLowerCase().startsWith('es')
  const promptTemplates = isSpanish ? PROMPT_TEMPLATES_ES : PROMPT_TEMPLATES_EN
  const stepTitles = React.useMemo(
    () => [
      t('agent_studio.steps.identity'),
      t('agent_studio.steps.behavior'),
      t('agent_studio.steps.tools'),
      t('agent_studio.steps.review'),
    ],
    [t]
  )
  const totalSteps = stepTitles.length

  React.useEffect(() => {
    if (createdAgent || step > AGENT_STUDIO_TOTAL_STEPS) {
      clearAgentStudioPersistedDraft()
      return
    }
    saveAgentStudioPersistedDraft({
      step: Math.min(Math.max(step, 1), AGENT_STUDIO_TOTAL_STEPS),
      visitedSteps: Array.from(visitedSteps)
        .filter((value) => value >= 1 && value <= AGENT_STUDIO_TOTAL_STEPS)
        .sort((a, b) => a - b),
      draft,
      editorMode,
      jsonDraft,
      selectedModel,
      visual,
      coverPrompt,
      coverStyle,
    })
  }, [createdAgent, step, visitedSteps, draft, editorMode, jsonDraft, selectedModel, visual, coverPrompt, coverStyle])

  const loadTools = React.useCallback(async () => {
    try {
      setToolsLoading(true)
      setLoadingAvailableModels(true)
      setAvailableModelsError(null)
      const [toolsResult, tagsResult, availableModelsResult] = await Promise.allSettled([
        toolsService.listActions({ enabled: true, includeGenerated: true }),
        tagService.getTags({ orderBy: 'name', order: 'asc' }),
        agentsService.listAvailableModels(),
      ])

      if (toolsResult.status === 'fulfilled') {
        const normalized = toolsResult.value.map((tool) => ({ ...tool, id: normalizeId(tool.id) }))
        const sorted = [...normalized].sort((a, b) => a.display_name.localeCompare(b.display_name, 'es'))
        setTools(sorted)
      } else {
        setTools([])
        setErrorMessage(toErrorMessage(toolsResult.reason))
      }

      if (tagsResult.status === 'fulfilled') {
        setAvailableTags(tagsResult.value || [])
      } else {
        setAvailableTags([])
      }

      if (availableModelsResult.status === 'fulfilled') {
        const payload = availableModelsResult.value as AgentAvailableModelsResponse
        setAvailableModelOptions(payload.items || [])
        setMediaProcessingModel(payload.media_processing_model || null)
      } else {
        setAvailableModelOptions([])
        setMediaProcessingModel(null)
        setAvailableModelsError(toErrorMessage(availableModelsResult.reason))
      }

      setToolIcons(loadToolIconsMap())
    } catch (error) {
      setErrorMessage(toErrorMessage(error))
    } finally {
      setToolsLoading(false)
      setLoadingAvailableModels(false)
    }
  }, [])

  React.useEffect(() => {
    setErrorMessage(null)
    void loadTools()
  }, [loadTools])

  const hasGeminiCredential = availableModelOptions.some((model) => model.provider === 'google')

  /** Brand-new draft only: pick first catalog model when nothing selected yet. */
  React.useEffect(() => {
    if (availableModelOptions.length === 0) return
    if (selectedModel.trim()) return
    const first = getFirstCatalogModelId(availableModelOptions)
    if (first) setSelectedModel(first)
  }, [availableModelOptions, selectedModel])

  const handleStudioModelChange = React.useCallback((modelId: string) => {
    setSelectedModel(modelId)
  }, [])

  const filteredTools = React.useMemo(() => {
    if (!searchTools.trim()) return tools

    const query = searchTools.trim().toLowerCase()
    return tools.filter((tool) => {
      return (
        tool.display_name.toLowerCase().includes(query) ||
        tool.name.toLowerCase().includes(query) ||
        (tool.description || '').toLowerCase().includes(query)
      )
    })
  }, [searchTools, tools])

  const publicRiskyToolsCount = React.useMemo(
    () =>
      draft.audience === 'public'
        ? tools.filter((tool) => draft.tool_ids.includes(normalizeId(tool.id)) && getToolVisibility(tool) !== 'external').length
        : 0,
    [draft.audience, draft.tool_ids, tools]
  )

  const selectedStudioTools = React.useMemo(
    () => tools.filter((tool) => draft.tool_ids.includes(normalizeId(tool.id))),
    [draft.tool_ids, tools]
  )

  const selectedToolDynamicKeys = React.useMemo(
    () => buildSelectedToolDynamicKeys(selectedStudioTools),
    [selectedStudioTools]
  )

  const duplicateToolDynamicKeys = React.useMemo(
    () => findDuplicateSelectedToolDynamicKeys(selectedStudioTools),
    [selectedStudioTools]
  )

  const duplicateToolDynamicKeySet = React.useMemo(
    () => new Set(duplicateToolDynamicKeys),
    [duplicateToolDynamicKeys]
  )

  const duplicateToolDynamicTokens = React.useMemo(
    () => duplicateToolDynamicKeys.map((key) => `{{tool.${key}}}`),
    [duplicateToolDynamicKeys]
  )

  const unknownReferencedTokens = React.useMemo(
    () =>
      findUnknownReferencedDynamicTokens(
        draft.base_prompt,
        selectedToolDynamicKeys,
        duplicateToolDynamicKeys
      ),
    [draft.base_prompt, duplicateToolDynamicKeys, selectedToolDynamicKeys]
  )

  const systemMentionSuggestions = React.useMemo(
    () =>
      buildSystemMentionSuggestions((token) =>
        t('agent_form.fields.prompt_alias_description', { token })
      ),
    [t]
  )

  const toolMentionSuggestions = React.useMemo(
    () =>
      buildToolMentionSuggestions(
        selectedStudioTools,
        selectedToolDynamicKeys
      ).filter((item) => !duplicateToolDynamicKeySet.has(item.token.replace(/^tool\./, ''))),
    [duplicateToolDynamicKeySet, selectedStudioTools, selectedToolDynamicKeys]
  )

  const variableMentionOptions = React.useMemo(() => {
    return [...systemMentionSuggestions, ...toolMentionSuggestions]
      .map((item) => ({
        token: item.token,
        label: item.label,
        description: item.description,
      }))
      .filter((item, index, all) => all.findIndex((other) => other.token === item.token) === index)
  }, [systemMentionSuggestions, toolMentionSuggestions])

  const step2ValidationMessage = React.useMemo(() => {
    if (!draft.base_prompt.trim()) return null
    if (unknownReferencedTokens.length > 0) {
      return t('agent_form.errors.invalid_tools_reference', { tools: unknownReferencedTokens.join(', ') })
    }
    if (duplicateToolDynamicTokens.length > 0) {
      return t('agent_form.errors.duplicate_dynamic_variables', {
        tools: duplicateToolDynamicTokens.join(', '),
      })
    }
    return null
  }, [draft.base_prompt, duplicateToolDynamicTokens, t, unknownReferencedTokens])

  const derivedAgentName = React.useMemo(
    () => slugifyAgentName(draft.display_name),
    [draft.display_name]
  )

  React.useEffect(() => {
    if (draft.name === derivedAgentName) return
    setDraft((prev) => ({ ...prev, name: derivedAgentName }))
  }, [draft.name, derivedAgentName])

  const mapStudioError = React.useCallback((error: unknown) => {
    const message = toErrorMessage(error)
    if (message === NAME_COLLISION_ERROR) {
      return t('agent_studio.internal_name_collision')
    }
    return message
  }, [t])

  const updateDraft = <K extends keyof AgentDraft>(field: K, value: AgentDraft[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  const updateBehaviorPrompt = React.useCallback((value: string) => {
    setDraft((prev) => ({ ...prev, base_prompt: value, custom_instructions: '' }))
  }, [])

  const updateDocumentIds = React.useCallback((documentIds: number[]) => {
    setDraft((prev) => ({ ...prev, document_ids: documentIds }))
  }, [])

  const handleDocumentCatalogUpdate = React.useCallback((items: AgentAvailableDocument[]) => {
    setDocumentCatalog((prev) => {
      const next = { ...prev }
      for (const item of items) {
        next[item.id] = {
          id: item.id,
          title: item.title,
          status: item.status,
          document_type: item.document_type,
        }
      }
      return next
    })
  }, [])

  const selectedPromptDocuments = React.useMemo(() => {
    return draft.document_ids
      .map((documentId) => documentCatalog[documentId])
      .filter((document): document is AgentDocument => Boolean(document))
  }, [documentCatalog, draft.document_ids])

  const selectedDocumentsPreview = React.useMemo(() => {
    return selectedPromptDocuments.slice(0, 5)
  }, [selectedPromptDocuments])

  React.useEffect(() => {
    // Legacy migration: collapse old split prompts into a single prompt field in onboarding UI.
    setDraft((prev) => {
      const legacyCustomInstructions = prev.custom_instructions.trim()
      if (!legacyCustomInstructions) return prev
      const mergedPrompt = mergePromptParts(prev.base_prompt, legacyCustomInstructions)
      return { ...prev, base_prompt: mergedPrompt, custom_instructions: '' }
    })
  }, [])

  const buildJsonSnapshot = React.useCallback(
    () =>
      JSON.stringify(
        {
          ...draft,
          custom_instructions: '',
        },
        null,
        2
      ),
    [draft]
  )

  const applyJsonDraft = (): boolean => {
    const validationMessage = validateAgentJsonText(jsonDraft)
    if (validationMessage) {
      setJsonError(validationMessage)
      return false
    }
    const parsed = JSON.parse(jsonDraft) as unknown
    const validationErrors = validateAgentJsonPayload(parsed)
    if (validationErrors.length > 0) {
      setJsonError(validationErrors.join('\n'))
      return false
    }
    const normalized = normalizeAgentJsonPayload(parsed)
    const mergedPrompt = mergePromptParts(normalized.base_prompt, normalized.custom_instructions)
    setDraft({ ...normalized, base_prompt: mergedPrompt, custom_instructions: '' })
    setJsonError(null)
    return true
  }

  const ensureJsonApplied = () => {
    if (editorMode !== 'json') return true
    return applyJsonDraft()
  }

  const toggleTool = (toolId: string) => {
    const normalizedToolId = normalizeId(toolId)
    setDraft((prev) => {
      const next = new Set(prev.tool_ids)
      if (next.has(normalizedToolId)) next.delete(normalizedToolId)
      else next.add(normalizedToolId)
      return { ...prev, tool_ids: Array.from(next) }
    })
  }

  const closeStudio = React.useCallback(() => {
    if (!createdAgent && step <= AGENT_STUDIO_TOTAL_STEPS) {
      setConfirmCloseOpen(true)
      return
    }
    navigate(`${langPrefix}/agents`)
  }, [createdAgent, step, langPrefix, navigate])

  const discardDraftAndClose = React.useCallback(() => {
    clearAgentStudioPersistedDraft()
    setConfirmCloseOpen(false)
    navigate(`${langPrefix}/agents`)
  }, [langPrefix, navigate])

  const keepDraftAndClose = React.useCallback(() => {
    setConfirmCloseOpen(false)
    navigate(`${langPrefix}/agents`)
  }, [langPrefix, navigate])

  const resolveCreatedAgent = React.useCallback(async (created: Partial<Agent>, expectedName: string): Promise<Agent | null> => {
    if (hasValidAgentId(created)) {
      return { ...(created as Agent), id: normalizeId(created.id) }
    }

    const allAgents = await agentsService.listAgents()
    const matched = allAgents.find((agent) => agent.name === expectedName)
    if (!matched?.id) return null
    const matchedAgent: Agent = matched

    try {
      const detailed = await agentsService.getAgent(normalizeId(matchedAgent.id))
      return { ...detailed, id: normalizeId(detailed.id) }
    } catch {
      return {
        id: normalizeId(matchedAgent.id),
        name: matchedAgent.name,
        display_name: matchedAgent.display_name,
        description: created.description || matchedAgent.description,
        is_enabled: matchedAgent.is_enabled,
        timezone: created.timezone || matchedAgent.timezone,
        base_prompt: created.base_prompt || matchedAgent.base_prompt,
        custom_instructions: created.custom_instructions || matchedAgent.custom_instructions,
        tools: created.tools || matchedAgent.tools,
        documents: created.documents || matchedAgent.documents,
        tool_ids: created.tool_ids || matchedAgent.tool_ids,
        document_ids: created.document_ids || matchedAgent.document_ids,
        created_at: created.created_at || matchedAgent.created_at,
        updated_at: created.updated_at || matchedAgent.updated_at,
      }
    }
  }, [])

  const goToStep = (targetStep: number) => {
    if (!ensureJsonApplied()) return
    if (targetStep < 1 || targetStep > totalSteps) return
    if (targetStep > 2 && step2ValidationMessage) {
      setErrorMessage(step2ValidationMessage)
      return
    }
    setErrorMessage(null)
    setStep(targetStep)
    setVisitedSteps((prev) => new Set(prev).add(targetStep))
  }

  const resetWizard = () => {
    clearAgentStudioPersistedDraft()
    setDraft(createInitialAgentDraft(DEFAULT_BASE_PROMPT))
    setDocumentCatalog({})
    setCreatedAgent(null)
    setEditingField(null)
    setErrorMessage(null)
    setSearchTools('')
    setVisual({})
    setCoverPrompt('')
    setCoverStyle('cinematic')
    setCoverError(null)
    setSelectedModel('')
    setEditorMode('form')
    setJsonDraft('')
    setJsonError(null)
    setVisitedSteps(new Set([1]))
    setStep(1)
    setConfirmCloseOpen(false)
  }

  const selectedModelInCatalog =
    availableModelOptions.length > 0 && isModelIdInCatalog(selectedModel, availableModelOptions)
  const validateStep1 =
    draft.display_name.trim().length > 0 &&
    derivedAgentName.length > 0 &&
    draft.timezone.trim().length > 0 &&
    availableModelOptions.length > 0 &&
    selectedModelInCatalog
  const validateStep2 = draft.base_prompt.trim().length > 0 && !step2ValidationMessage
  const isStepValid = React.useCallback((itemStep: number) => {
    if (itemStep === 1) return validateStep1
    if (itemStep === 2) return validateStep2
    if (itemStep === 3) return true
    if (itemStep === 4) return validateStep1 && validateStep2
    return false
  }, [validateStep1, validateStep2])

  const progress = step > totalSteps ? 100 : Math.round((step / totalSteps) * 100)

  const createAgent = async () => {
    if (!ensureJsonApplied()) return
    if (step2ValidationMessage) {
      setErrorMessage(step2ValidationMessage)
      return
    }
    setCreatingAgent(true)
    setErrorMessage(null)
    const parsedModel = parseModelId(selectedModel)
    if (!parsedModel) {
      setErrorMessage(t('agent_studio.model_invalid_selection'))
      setCreatingAgent(false)
      return
    }
    const payload = {
      name: derivedAgentName,
      display_name: draft.display_name.trim(),
      description: draft.description.trim(),
      is_enabled: draft.is_enabled,
      timezone: draft.timezone.trim(),
      prompt_mode: 'isolated' as const,
      base_prompt: draft.base_prompt.trim(),
      custom_instructions: '',
      tool_ids: draft.tool_ids,
      document_ids: draft.document_ids,
      audience: draft.audience,
      llm_provider: parsedModel.provider,
      llm_model: parsedModel.model,
      openai_reasoning_effort: openaiReasoningEffortForApiPayload(parsedModel.provider),
      fallback_vision_model: 'gemini-2.5-pro',
      fallback_video_model: 'gemini-2.5-pro',
      fallback_pdf_model: 'gemini-2.5-pro',
    }
    let resolvedAgent: Agent | null = null

    try {
      const created = await agentsService.createAgent(payload)
      resolvedAgent = await resolveCreatedAgent(created, payload.name)
      if (!hasValidAgentId(resolvedAgent)) {
        throw new Error(t('agent_studio.created_id_error'))
      }
    } catch (error) {
      setErrorMessage(mapStudioError(error))
      setCreatingAgent(false)
      return
    }
    try {
      setCreatedAgent(resolvedAgent)
      if (resolvedAgent?.id) {
        // Do not block the create flow if local persistence fails.
        try {
          saveAgentVisual(normalizeId(resolvedAgent.id), visual)
        } catch {
          // ignore localStorage persistence errors
        }
      }
      clearAgentStudioPersistedDraft()
      setStep(totalSteps + 1)
      setConfettiSeed((prev) => prev + 1)
    } finally {
      setCreatingAgent(false)
    }
  }

  const finalizeAndExit = async () => {
    if (!hasValidAgentId(createdAgent)) {
      navigate(`${langPrefix}/agents`)
      return
    }
    setFinishing(true)
    try {
      navigate(`${langPrefix}/agents`)
    } finally {
      setFinishing(false)
    }
  }

  const stepKey = `agent-studio-step-${step}`

  const handleGenerateCover = async () => {
    if (!coverPrompt.trim()) return
    try {
      setCoverGenerating(true)
      setCoverError(null)
      const response = await agentsService.generateAgentCover(coverPrompt.trim(), coverStyle)
      setVisual((prev) => ({ ...prev, coverUrl: response.image_url, icon: undefined }))
    } catch (error) {
      setCoverError(mapStudioError(error))
    } finally {
      setCoverGenerating(false)
    }
  }

  const content = (
    <div className="fixed inset-0 z-40 flex bg-background">
      {step === totalSteps + 1 && <CelebrationConfetti seed={confettiSeed} />}

      {/* Mobile header — shown instead of sidebar on small screens */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-1 border-b bg-background px-2 py-2 h-12">
          <Button variant="ghost" size="icon-xs" className="shrink-0" onClick={closeStudio}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">
              {step <= totalSteps ? `${step}/${totalSteps}` : '✓'}
            </span>
            <div className="h-1.5 w-16 rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex-1" />
          <Button variant="ghost" size="xs" onClick={() => navigate(`${langPrefix}/agent-studio/call-agents`)}>
            <PhoneCall className="h-3.5 w-3.5" />
          </Button>
          {step <= totalSteps && (
            <Button variant="outline" size="xs" className="shrink-0" onClick={() => {
              if (editorMode === 'form') { const snapshot = buildJsonSnapshot(); setJsonDraft(snapshot); setJsonError(null); setEditorMode('json'); return; }
              setEditorMode('form');
            }}>
              {editorMode === 'form' ? 'JSON' : 'Form'}
            </Button>
          )}
          <Button variant="ghost" size="icon-xs" className="shrink-0" disabled={step <= 1} onClick={() => goToStep(step - 1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon-xs" className="shrink-0" disabled={step > totalSteps} onClick={() => goToStep(step + 1)}>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Sidebar — desktop only */}
      {!isMobile && (
      <div className="flex w-[290px] shrink-0 flex-col border-r border-border bg-muted/20">
        <div className="p-3 pb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeStudio}
            className="w-full justify-start gap-2 rounded-lg text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('agent_studio.back_to_agents')}
          </Button>
        </div>

        <div className="px-4 pb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.nav_title')}</p>
            <h1 className="mt-1 text-lg font-semibold leading-tight">{t('agent_studio.nav_create')}</h1>
          </div>
        </div>

        <div className="px-4 pb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('agent_studio.progress')} {progress}%
        </div>
        <div className="mx-4 h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>

        <ScrollArea className="mt-3 flex-1">
          <div className="px-3 pb-3">
            {stepTitles.map((title, index) => {
              const itemStep = index + 1
              const status = getWizardStepStatus({
                itemStep,
                currentStep: step > totalSteps ? totalSteps : step,
                visitedSteps,
                isStepValid,
              })

              return (
                <WizardSidebarStep
                  key={title}
                  onClick={() => goToStep(itemStep)}
                  status={status}
                  stepLabel={`${t('agent_studio.step')} ${itemStep}`}
                  title={title}
                />
              )
            })}
          </div>
        </ScrollArea>

        <div className="border-t border-border p-3">
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{t('agent_studio.summary_title')}</p>
            <p className="mt-1">{t('agent_studio.summary_tools')}: {draft.tool_ids.length}</p>
            <p>{t('agent_studio.summary_documents')}: {draft.document_ids.length}</p>
            <p>{t('agent_studio.summary_audience')}: {draft.audience === 'public' ? t('agent_studio.audience_public') : t('agent_studio.audience_internal')}</p>
            <p>{t('agent_studio.summary_timezone')}: {draft.timezone || DEFAULT_AGENT_TIMEZONE}</p>
            <p>{t('agent_studio.summary_channels_later')}</p>
          </div>
        </div>
      </div>
      )}

      <div className={cn("flex min-w-0 flex-1 flex-col", isMobile && "pt-16")}>
        <div className={cn("flex h-12 items-center justify-between border-b border-border px-4", isMobile && "hidden")}>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-lg">{t('agent_studio.nav_title')}</Badge>
            <span className="text-sm text-muted-foreground">
              {step <= totalSteps ? `${t('agent_studio.step')} ${step} ${t('agent_studio.of')} ${totalSteps}` : t('agent_studio.completed')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="xs"
             
              onClick={() => navigate(`${langPrefix}/agent-studio/call-agents`)}
            >
              <PhoneCall className="mr-2 h-4 w-4" />
              {t('agent_studio.call_agents_link')}
            </Button>
            {step <= totalSteps && (
              <Button
                variant="outline"
                size="xs"
               
                onClick={() => {
                  if (editorMode === 'form') {
                    const snapshot = buildJsonSnapshot()
                    setJsonDraft(snapshot)
                    setJsonError(null)
                    setEditorMode('json')
                    return
                  }
                  if (!applyJsonDraft()) return
                  setEditorMode('form')
                }}
              >
                {editorMode === 'form' ? 'Editar JSON' : 'Editar formulario'}
              </Button>
            )}
            <Button variant="ghost" size="icon-xs" onClick={closeStudio} title={t('agent_studio.close')}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {errorMessage && (
          <div className="px-6 pt-4">
            <Alert variant="destructive">
              <AlertTitle>{t('agent_studio.error')}</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex-1 min-h-0">
          <AnimatePresence mode="wait">
            {editorMode === 'json' && step <= totalSteps && (
              <motion.div
                key="agent-studio-json"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title="Editar agente en JSON"
                  subtitle="Modifica el draft completo del agente y aplica los cambios."
                  footer={
                    <div className="flex items-center justify-between">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (!applyJsonDraft()) return
                          setEditorMode('form')
                        }}
                      >
                        Volver al formulario
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            const snapshot = buildJsonSnapshot()
                            setJsonDraft(snapshot)
                            setJsonError(null)
                          }}
                        >
                          Reset desde formulario
                        </Button>
                        <Button onClick={applyJsonDraft}>
                          Aplicar JSON
                        </Button>
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-2">
                    <Label htmlFor="agent-json-editor">Payload JSON</Label>
                    <Textarea
                      id="agent-json-editor"
                      value={jsonDraft}
                      onChange={(event) => {
                        setJsonDraft(event.target.value)
                        if (jsonError) setJsonError(null)
                      }}
                      className="min-h-[560px] font-mono text-xs"
                      placeholder='{"name":"lead_assistant","display_name":"Lead assistant"}'
                    />
                    {jsonError && <p className="whitespace-pre-line text-sm text-destructive">{jsonError}</p>}
                  </div>
                </StepLayout>
              </motion.div>
            )}

            {editorMode === 'form' && step === 1 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title={t('agent_studio.step1_title')}
                  subtitle={t('agent_studio.step1_subtitle')}
                  footer={
                    <div className="flex justify-end">
                      <Button size="lg" onClick={() => goToStep(2)} disabled={!validateStep1}>
                        {t('agent_studio.next')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  }
                >
                  <div className="mb-6">
                    <AgentVisualEditor
                      visual={visual}
                      onVisualChange={setVisual}
                      prompt={coverPrompt}
                      onPromptChange={setCoverPrompt}
                      style={coverStyle}
                      onStyleChange={setCoverStyle}
                      generating={coverGenerating}
                      generateError={coverError}
                      onGenerate={() => void handleGenerateCover()}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="display-name" className="text-sm">{t('agent_studio.visible_name')} *</Label>
                      <Input
                        id="display-name"
                        className="h-12 text-sm"
                        value={draft.display_name}
                        onChange={(event) => {
                          const value = event.target.value
                          updateDraft('display_name', value)
                        }}
                        placeholder={t('agent_studio.visible_name_placeholder')}
                      />
                      <Label className="text-sm">{t('agent_studio.internal_name')} *</Label>
                      <div className="rounded-lg border bg-muted/30 px-3 py-2 font-mono text-sm text-foreground">
                        {derivedAgentName || '-'}
                      </div>
                      <p className="text-xs text-muted-foreground">{t('agent_studio.internal_name_auto_help')}</p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label htmlFor="description" className="text-sm">{t('agent_studio.description')}</Label>
                    <Textarea
                      id="description"
                      rows={5}
                      value={draft.description}
                      onChange={(event) => updateDraft('description', event.target.value)}
                      placeholder={t('agent_studio.description_placeholder')}
                    />
                    <p className="text-xs text-muted-foreground">{t('agent_studio.description_help')}</p>
                  </div>

                  <div className="mt-6">
                    <Label className="text-sm">Modelo</Label>
                    <Select
                      value={
                        isModelIdInCatalog(selectedModel, availableModelOptions) ? selectedModel : undefined
                      }
                      onValueChange={handleStudioModelChange}
                      disabled={loadingAvailableModels || availableModelOptions.length === 0}
                    >
                      <SelectTrigger className="mt-2 h-11">
                        <SelectValue
                          placeholder={
                            loadingAvailableModels
                              ? t('agent_studio.model_loading_options')
                              : t('agent_studio.model_missing_provider_keys')
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModelOptions.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedModel.trim() &&
                      !loadingAvailableModels &&
                      availableModelOptions.length > 0 &&
                      !isModelIdInCatalog(selectedModel, availableModelOptions) && (
                        <Alert className="mt-3 border-warning/30 bg-warning/10">
                          <AlertTitle>{t('agent_studio.model_unavailable_title')}</AlertTitle>
                          <AlertDescription>
                            {t('agent_studio.model_unavailable_body', { model: selectedModel })}
                          </AlertDescription>
                        </Alert>
                      )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('agent_studio.models_by_configured_providers')}
                    </p>
                    {availableModelsError && (
                      <p className="mt-1 text-xs text-destructive">
                        {t('agent_studio.provider_status_error', { error: availableModelsError })}
                      </p>
                    )}
                  </div>

                  <div className="mt-6">
                    <Label className="text-sm">{t('agent_studio.media_model_label')}</Label>
                    <Select value={mediaProcessingModel?.id} disabled>
                      <SelectTrigger className="mt-2 h-11">
                        <SelectValue placeholder={t('agent_studio.model_loading_options')} />
                      </SelectTrigger>
                      <SelectContent>
                        {mediaProcessingModel && (
                          <SelectItem value={mediaProcessingModel.id}>{mediaProcessingModel.label}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('agent_studio.media_model_help')}
                    </p>
                    {!loadingAvailableModels && !hasGeminiCredential && (
                      <Alert className="mt-3 border-warning/30 bg-warning/10">
                        <AlertTitle>{t('agent_studio.missing_gemini_key_title')}</AlertTitle>
                        <AlertDescription>
                          {t('agent_studio.missing_gemini_key_body')}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <div className="mt-6">
                    <AgentTimezonePicker
                      id="studio-agent-timezone"
                      value={draft.timezone}
                      onChange={(timezone) => updateDraft('timezone', timezone)}
                      label={t('agent_studio.timezone_label')}
                      helpText={t('agent_studio.timezone_help')}
                      placeholder={t('agent_studio.timezone_placeholder')}
                      recommendedLabel={t('agent_studio.timezone_recommended')}
                      allLabel={t('agent_studio.timezone_all')}
                      currentTimeLabel={t('agent_studio.timezone_current_time')}
                      defaultBadgeLabel={t('agent_studio.timezone_default_badge')}
                      required
                    />
                  </div>

                  <div className="mt-6">
                    <Label className="text-sm">{t('agent_studio.audience_label')}</Label>
                    <Select value={draft.audience} onValueChange={(value: AgentAudience) => updateDraft('audience', value)}>
                      <SelectTrigger className="mt-2 h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">{t('agent_studio.audience_internal')}</SelectItem>
                        <SelectItem value="public">{t('agent_studio.audience_public')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-xs text-muted-foreground">{t('agent_studio.audience_help')}</p>
                  </div>

                  <div className="mt-6">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <Label htmlFor="agent-enabled" className="text-sm font-medium">{t('agent_studio.activate_on_create')}</Label>
                        <p className={cn('text-xs font-medium', draft.is_enabled ? 'text-success' : 'text-warning')}>
                          {draft.is_enabled ? t('agent_studio.enabled') : t('agent_studio.disabled')}
                        </p>
                        <p className="text-xs text-muted-foreground">{t('agent_studio.activate_help')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            draft.is_enabled ? 'border-success/30 text-success' : 'border-warning/30 text-warning'
                          )}
                        >
                          {draft.is_enabled ? 'ON' : 'OFF'}
                        </Badge>
                        <Switch
                          id="agent-enabled"
                          checked={draft.is_enabled}
                          onCheckedChange={(checked) => updateDraft('is_enabled', checked)}
                        />
                      </div>
                    </div>
                  </div>
                </StepLayout>
              </motion.div>
            )}

            {editorMode === 'form' && step === 2 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title={t('agent_studio.step2_title')}
                  subtitle={t('agent_studio.step2_subtitle')}
                  footer={
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="lg" onClick={() => goToStep(1)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('agent_studio.back')}
                      </Button>
                      <Button size="lg" onClick={() => goToStep(3)} disabled={!validateStep2}>
                        {t('agent_studio.next')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  }
                >
                  <div>
                    <Label htmlFor="base-prompt" className="text-sm">{t('agent_studio.prompt')} *</Label>
                    <PromptRichEditor
                      id="base-prompt"
                      ariaLabel={t('agent_studio.prompt')}
                      className="mt-2"
                      value={draft.base_prompt}
                      onChange={updateBehaviorPrompt}
                      tools={selectedStudioTools.map((tool) => ({
                        id: normalizeId(tool.id),
                        name: tool.name,
                        displayName: tool.display_name,
                      }))}
                      tags={availableTags.map((tag) => ({
                        id: String(tag.id),
                        name: tag.name,
                        color: tag.color,
                        description: tag.description,
                      }))}
                      documents={selectedPromptDocuments.map((document) => ({
                        id: String(document.id),
                        title: document.title,
                        documentType: document.document_type,
                      }))}
                      variableOptions={variableMentionOptions}
                    />
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('agent_form.fields.prompt_help')} <code>{'{{system.name}}'}</code>, <code>{'{{system.conversation_id}}'}</code>, <code>{'{{tool.order_id}}'}</code>, <code>@tool_name</code>, <code>@tag:123</code>, <code>@doc:101</code>.
                    </p>
                    {unknownReferencedTokens.length > 0 && (
                      <Alert className="mt-3 border-warning/30 bg-warning/10">
                        <AlertTitle>{t('agent_form.fields.prompt_invalid_tools_title')}</AlertTitle>
                        <AlertDescription>
                          {t('agent_form.fields.prompt_invalid_tools_body', { tools: unknownReferencedTokens.join(', ') })}
                        </AlertDescription>
                      </Alert>
                    )}
                    {duplicateToolDynamicTokens.length > 0 && (
                      <Alert className="mt-3 border-destructive/30 bg-destructive/10">
                        <AlertTitle>{t('agent_form.fields.prompt_duplicate_variables_title')}</AlertTitle>
                        <AlertDescription>
                          {t('agent_form.fields.prompt_duplicate_variables_body', {
                            tools: duplicateToolDynamicTokens.join(', '),
                          })}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  <div className="mt-6">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{t('agent_studio.documents_title')}</p>
                        <p className="text-xs text-muted-foreground">{t('agent_studio.documents_help')}</p>
                      </div>
                      <Badge variant="secondary">{draft.document_ids.length} {t('agent_studio.selected').toLowerCase()}</Badge>
                    </div>
                    <AgentDocumentsSelector
                      selectedDocumentIds={draft.document_ids}
                      onChange={updateDocumentIds}
                      onCatalogUpdate={handleDocumentCatalogUpdate}
                      listHeightClass="h-[28vh]"
                      labels={{
                        selected: t('agent_studio.selected_documents'),
                        searchPlaceholder: t('agent_studio.search_document'),
                        loading: t('agent_studio.loading_documents'),
                        empty: t('agent_studio.no_documents_filtered'),
                        category: t('agent_studio.documents_category'),
                        subcategory: t('agent_studio.documents_subcategory'),
                      }}
                    />
                  </div>

                  <div className="mt-6">
                    <div className="mb-2 flex items-center gap-2">
                      <WandSparkles className="h-4 w-4 text-primary" />
                      <p className="text-sm font-medium">{t('agent_studio.templates')}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {promptTemplates.map((template) => (
                        <Button
                          key={template.label}
                          type="button"
                          variant="outline"
                          onClick={() => updateBehaviorPrompt(template.value)}
                        >
                          {template.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Guardrails temporarily paused while behavior prompt is consolidated into one single field. */}
                  {/* <div className="mt-6">
                    <AgentGuardrailsEditor value={guardrails} onChange={setGuardrails} />
                  </div> */}
                </StepLayout>
              </motion.div>
            )}

            {editorMode === 'form' && step === 3 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title={t('agent_studio.step3_title')}
                  subtitle={t('agent_studio.step3_subtitle')}
                  footer={
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="lg" onClick={() => goToStep(2)}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('agent_studio.back')}
                      </Button>
                      <Button size="lg" onClick={() => goToStep(4)}>
                        {t('agent_studio.next')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  }
                >
                  <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('agent_studio.selected_tools')}</p>
                      <p className="text-2xl font-semibold">{draft.tool_ids.length}</p>
                    </div>
                    <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
                      <Input
                        className="h-11 md:w-[320px]"
                        value={searchTools}
                        onChange={(event) => setSearchTools(event.target.value)}
                        placeholder={t('agent_studio.search_tool')}
                      />
                      <Button type="button" variant="outline" onClick={() => setToolFormOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('agent_studio.create_tool')}
                      </Button>
                    </div>
                  </div>

                  <div>
                    {draft.audience === 'public' && publicRiskyToolsCount > 0 && (
                      <Alert className="mb-4 border-warning/30 bg-warning/10">
                        <AlertTitle>{t('agent_studio.public_agent_warning_title')}</AlertTitle>
                        <AlertDescription>
                          {t('agent_studio.public_agent_warning_body', { count: publicRiskyToolsCount })}
                        </AlertDescription>
                      </Alert>
                    )}
                    {toolsLoading ? (
                      <p className="text-sm text-muted-foreground">{t('agent_studio.loading_tools')}</p>
                    ) : filteredTools.length === 0 ? (
                      <div className="py-8 text-center">
                        <p className="text-sm text-muted-foreground">{t('agent_studio.no_tools_filtered')}</p>
                        <Button className="mt-3" variant="outline" onClick={() => setToolFormOpen(true)}>
                          {t('agent_studio.create_tool')}
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-2 md:grid-cols-2">
                        {filteredTools.map((tool) => {
                          const toolId = normalizeId(tool.id)
                          const selected = draft.tool_ids.includes(toolId)
                          const visibility = getToolVisibility(tool)
                          const bloqueada = herramientaBloqueadaPorAudiencia(draft.audience, visibility, selected)
                          return (
                            <Button
                              key={toolId}
                              variant="outline"
                              size="tarjeta"
                              aria-pressed={selected}
                              disabled={bloqueada}
                              title={bloqueada ? t('agent_studio.internal_tool_needs_internal_audience') : undefined}
                              onClick={() => toggleTool(toolId)}
                              className={cn(
                                'transition',
                                bloqueada && 'cursor-not-allowed opacity-50',
                                selected
                                  ? 'border-primary/30 bg-primary/10'
                                  : 'border-border bg-background hover:bg-muted/30'
                              )}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-muted/40">
                                      {toolIcons[toolId]?.icon ? (
                                        <AgentVisualIcon value={toolIcons[toolId]?.icon} colorClass={toolIcons[toolId]?.colorClass} className="h-3.5 w-3.5" />
                                      ) : (
                                        <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                                      )}
                                    </span>
                                    <p className="font-medium">{tool.display_name}</p>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'h-5 px-1.5 text-[10px]',
                                        visibility === 'internal' && 'border-warning/30 text-warning',
                                        visibility === 'mixed' && 'border-primary/30 text-primary',
                                        visibility === 'external' && 'border-success/30 text-success'
                                      )}
                                    >
                                      {visibility === 'internal'
                                        ? t('agent_studio.visibility_internal')
                                        : visibility === 'mixed'
                                          ? t('agent_studio.visibility_mixed')
                                          : t('agent_studio.visibility_external')}
                                    </Badge>
                                  </div>
                                  <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{tool.execution_type}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {selected && <span className="text-xs font-medium text-primary">{t('agent_studio.selected')}</span>}
                                  <span
                                  className={cn(
                                    'inline-flex h-5 w-5 items-center justify-center rounded-full border',
                                    selected ? 'border-primary bg-primary/10 text-primary' : 'border-border text-transparent'
                                  )}
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  </span>
                                </div>
                              </div>
                              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{tool.description || tool.name}</p>
                            </Button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </StepLayout>
              </motion.div>
            )}

            {editorMode === 'form' && step === 4 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <StepLayout isMobile={isMobile}
                  title={t('agent_studio.step4_title')}
                  subtitle={t('agent_studio.step4_subtitle')}
                  footer={
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="lg" onClick={() => goToStep(3)} disabled={creatingAgent}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('agent_studio.back')}
                      </Button>
                      <Button size="lg" onClick={createAgent} disabled={creatingAgent || !validateStep1 || !validateStep2}>
                        {creatingAgent ? t('agent_studio.creating_agent') : t('agent_studio.create_agent')}
                        {!creatingAgent && <Sparkles className="ml-2 h-4 w-4" />}
                      </Button>
                    </div>
                  }
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-lg border bg-muted/10 p-4">
                      <div className="mb-4 h-20 w-full overflow-hidden rounded-lg bg-background/60">
                        <AgentAvatar visual={visual} fallbackClassName="bg-muted/30" />
                      </div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.visible_name')}</p>
                      {editingField === 'display_name' ? (
                        <Input
                          autoFocus
                          value={draft.display_name}
                          onBlur={() => setEditingField(null)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === 'Escape') {
                              setEditingField(null)
                            }
                          }}
                          onChange={(event) => {
                            const value = event.target.value
                            updateDraft('display_name', value)
                          }}
                          className="mt-2"
                        />
                      ) : (
                        <Button
                          variant="link"
                          className="mt-1 justify-start text-foreground"
                          onClick={() => setEditingField('display_name')}
                        >
                          {/* El tamano lo lleva el texto, no el boton: un enlace
                              hereda el del texto que lo rodea. */}
                          <span className="text-lg font-semibold">{draft.display_name || '-'}</span>
                        </Button>
                      )}
                      <Separator className="my-3" />

                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.internal_name')}</p>
                      <p className="mt-1 font-mono text-sm text-primary">{derivedAgentName || '-'}</p>
                      <Separator className="my-3" />

                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.status')}</p>
                      <Button
                        size="xs"
                        variant={draft.is_enabled ? 'secondary' : 'ghost'}
                        className="mt-1 rounded-full"
                        onClick={() => updateDraft('is_enabled', !draft.is_enabled)}
                      >
                        {draft.is_enabled ? t('agent_studio.enabled') : t('agent_studio.disabled')}
                      </Button>
                      <Separator className="my-3" />
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Modelo</p>
                      <p className="mt-1 text-sm text-primary">{availableModelOptions.find((m) => m.id === selectedModel)?.label || selectedModel}</p>
                      <Separator className="my-3" />
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.summary_timezone')}</p>
                      <p className="mt-1 font-mono text-sm text-primary">{draft.timezone || DEFAULT_AGENT_TIMEZONE}</p>
                      <Separator className="my-3" />
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('agent_studio.summary_audience')}</p>
                      <Badge variant="outline" className="mt-1">
                        {draft.audience === 'public' ? t('agent_studio.audience_public') : t('agent_studio.audience_internal')}
                      </Badge>
                    </div>

                    <div className="rounded-lg border bg-muted/10 p-4">
                      <p className="text-sm font-semibold">{t('agent_studio.selected_tools_title')}</p>
                      {draft.tool_ids.length === 0 ? (
                        <p className="mt-2 text-sm text-muted-foreground">{t('agent_studio.no_tools_selected')}</p>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {tools
                            .filter((tool) => draft.tool_ids.includes(tool.id))
                            .map((tool) => (
                              <Badge key={tool.id} variant="secondary">{tool.display_name}</Badge>
                            ))}
                        </div>
                      )}

                      <Separator className="my-4" />
                      <p className="text-sm font-semibold">{t('agent_studio.selected_documents_title')}</p>
                      {draft.document_ids.length === 0 ? (
                        <p className="mt-2 text-sm text-muted-foreground">{t('agent_studio.no_documents_selected')}</p>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedDocumentsPreview.map((document) => (
                            <Badge key={document.id} variant="secondary">{document.title}</Badge>
                          ))}
                          {draft.document_ids.length > selectedDocumentsPreview.length && (
                            <Badge variant="outline">+{draft.document_ids.length - selectedDocumentsPreview.length}</Badge>
                          )}
                        </div>
                      )}

                      <Separator className="my-4" />
                      <p className="text-sm font-semibold">{t('agent_studio.description')}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{draft.description || t('agent_studio.no_description')}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t('agent_studio.description_internal_note')}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground">
                    {t('agent_studio.channels_after_create_note')}
                  </p>
                </StepLayout>
              </motion.div>
            )}

            {step === totalSteps + 1 && (
              <motion.div
                key={stepKey}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                <div className="h-full min-h-0">
                  <ScrollArea className="h-full">
                  <div className="mx-auto flex min-h-full w-full max-w-2xl items-start justify-center px-6 py-8">
                    <div className="w-full py-8 text-center">
                    <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
                      <CheckCircle2 className="h-8 w-8 text-success" />
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight">{t('agent_studio.created_title')}</h2>
                    <p className="mt-2 text-muted-foreground">
                      <span className="font-medium text-foreground">{createdAgent?.display_name || draft.display_name}</span> {t('agent_studio.created_subtitle_suffix')}
                    </p>

                    <div className="mt-6 grid gap-2 md:grid-cols-2">
                      <Button variant="outline" onClick={() => void finalizeAndExit()} disabled={finishing}>
                        {finishing ? t('agent_studio.saving') : t('agent_studio.finish')}
                      </Button>
                      <Button className="md:col-span-2" onClick={resetWizard}>{t('agent_studio.create_another')}</Button>
                    </div>
                  </div>
                  </div>
                  </ScrollArea>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ToolForm
        open={toolFormOpen}
        onOpenChange={setToolFormOpen}
        onSuccess={() => {
          void loadTools()
        }}
      />
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('agent_studio.exit_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('agent_studio.exit_confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('agent_studio.exit_cancel')}</AlertDialogCancel>
            <Button variant="destructive" onClick={discardDraftAndClose}>
              {t('agent_studio.exit_discard')}
            </Button>
            <AlertDialogAction onClick={keepDraftAndClose}>
              {t('agent_studio.exit_save_draft')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )

  return createPortal(content, document.body)
}
